import {normalizeToAsciiSnakeCase} from "@pg-mono/string-utils";

import {hitGoogleTagManager} from "./hit_google_tag_manager";

// Releated Task: RP-319

interface IRegionAncestorLike {
    slug?: string;
    name?: string;
}

interface IRegionStatsLike {
    region_type_city?: IRegionAncestorLike | null;
    region_type_district?: IRegionAncestorLike | null;
    region_type_town?: IRegionAncestorLike | null;
}

interface IUserSegmentRegionLike {
    slug?: string;
    name?: string;
    full_name_reverted?: string;
    stats?: IRegionStatsLike | null;
    parent?: {
        slug?: string;
        name?: string;
    } | null;
}

interface IOfferTypeSegmentConfig {
    map: Partial<Record<number, string>>;
    defaultTypes: number[];
}

interface IHitUserSegmentBudgetParams {
    priceUpper?: unknown;
    instalment?: unknown;
}

const DEFAULT_OFFER_TYPE_SEGMENT_MAP: Partial<Record<number, string>> = {
    1: "user_segment_offer_type_mieszkania",
    2: "user_segment_offer_type_domy",
    3: "user_segment_offer_type_lokale",
    4: "user_segment_offer_type_pod_inwestycje"
};

const DEFAULT_OFFER_TYPE_SEGMENT_TYPES = [1, 2];

// -----------------------------------------------------------------------------
// Shared primitives
// -----------------------------------------------------------------------------

function parsePriceUpper(value: unknown) {
    if (value === undefined || value === null || value === "") return null;

    const parsed = typeof value === "number" ? value : parseInt(String(value).replace(/\s/g, ""), 10);

    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function hasPositiveInstalmentFilter(instalment: unknown) {
    return instalment !== undefined && instalment !== "" && instalment !== null && Number(instalment) > 0;
}

// -----------------------------------------------------------------------------
// Event name builders: budget
// -----------------------------------------------------------------------------

function budgetSegmentEvent(priceUpper: number | null) {
    if (priceUpper === null) return "user_segment_budget_bez_limitu";
    if (priceUpper <= 500_000) return "user_segment_budget_0_500k";
    if (priceUpper <= 1_000_000) return "user_segment_budget_500k_1mln";
    if (priceUpper <= 1_500_000) return "user_segment_budget_1mln_1_5mln";
    if (priceUpper <= 2_000_000) return "user_segment_budget_1_5mln_2mln";

    return "user_segment_budget_2mln_plus";
}

// -----------------------------------------------------------------------------
// Event name builders: rooms
// -----------------------------------------------------------------------------

function roomCountToSegmentEvent(roomCount: number) {
    if (roomCount >= 5) return "user_segment_rooms_5_plus";
    if (roomCount <= 1) return "user_segment_rooms_1";
    if (roomCount === 2) return "user_segment_rooms_2";
    if (roomCount === 3) return "user_segment_rooms_3";

    return "user_segment_rooms_4";
}

function roomsSegmentEvents(params: {lower?: number | string | null; upper?: number | string | null}, maxRooms = 5, minRooms = 1) {
    const rawUpper = params.upper !== undefined && params.upper !== "" ? Number(params.upper) : NaN;
    const rawLower = params.lower !== undefined && params.lower !== "" ? Number(params.lower) : NaN;

    const hasUpperBound = Number.isFinite(rawUpper);
    const hasLowerBound = Number.isFinite(rawLower);

    if (!hasUpperBound && !hasLowerBound) return [];

    const collectRange = (minRoomCount: number, maxRoomCount: number) => {
        if (minRoomCount > maxRoomCount) return [];

        const eventNames = new Set<string>();

        for (let roomCount = minRoomCount; roomCount <= maxRoomCount; roomCount++) {
            eventNames.add(roomCountToSegmentEvent(roomCount));
        }

        return Array.from(eventNames);
    };

    if (hasLowerBound && hasUpperBound) {
        return collectRange(Math.round(Math.min(rawLower, rawUpper)), Math.round(Math.max(rawLower, rawUpper)));
    }

    if (hasLowerBound && !hasUpperBound) {
        return collectRange(Math.round(rawLower), maxRooms);
    }

    return collectRange(minRooms, Math.round(rawUpper));
}

// -----------------------------------------------------------------------------
// Event name builders: location
// -----------------------------------------------------------------------------

function segmentLabelFromEntity(value: {name?: string | null; slug?: string | null} | null | undefined) {
    if (!value) return "";

    const raw = value.name?.trim() || value.slug?.trim();

    return raw ? normalizeToAsciiSnakeCase(raw) : "";
}

/** When `full_name_reverted` is "District, City, Voivodeship", the city is the middle segment. */
function segmentCityFromFullNameReverted(fullNameReverted: string | null | undefined) {
    const parts = (fullNameReverted ?? "")
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);
    if (parts.length < 3) return "";

    return normalizeToAsciiSnakeCase(parts[1]);
}

/** When `full_name_reverted` is "District, City, Voivodeship", the district is the first segment. */
function segmentDistrictFromFullNameReverted(fullNameReverted: string | null | undefined) {
    const parts = (fullNameReverted ?? "")
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);
    if (parts.length < 3) return "";

    return normalizeToAsciiSnakeCase(parts[0]);
}

function stripCityTokenPrefix(districtToken: string, cityToken: string) {
    if (!districtToken || !cityToken) return districtToken;

    const prefix = `${cityToken}_`;
    return districtToken.startsWith(prefix) ? districtToken.slice(prefix.length) : districtToken;
}

function segmentDistrictLabelFromEntity(value: {name?: string | null; slug?: string | null} | null | undefined, cityToken: string) {
    if (!value) return "";

    const name = value.name?.trim();
    if (name) return normalizeToAsciiSnakeCase(name);

    const slug = value.slug?.trim();
    if (!slug) return "";

    const hyphenIdx = slug.indexOf("-");
    if (hyphenIdx > 0 && cityToken) {
        const slugCity = normalizeToAsciiSnakeCase(slug.slice(0, hyphenIdx));
        if (slugCity === cityToken) {
            const districtSlug = slug.slice(hyphenIdx + 1);
            if (districtSlug) return normalizeToAsciiSnakeCase(districtSlug);
        }
    }

    return stripCityTokenPrefix(normalizeToAsciiSnakeCase(slug), cityToken);
}

function locationEntrySegmentEvents(regions: (IUserSegmentRegionLike | null | undefined)[]) {
    const cityEventNames = new Set<string>();
    const districtEventNames = new Set<string>();

    for (const region of regions) {
        if (!region) continue;

        const stats = region.stats;
        const cityOrTown = stats?.region_type_city ?? stats?.region_type_town;

        const cityToken =
            segmentLabelFromEntity(cityOrTown) ||
            segmentCityFromFullNameReverted(region.full_name_reverted) ||
            segmentLabelFromEntity(region) ||
            segmentLabelFromEntity(region.parent);

        if (cityToken) {
            cityEventNames.add(`user_segment_city_${cityToken}`);
        }

        const districtToken = stripCityTokenPrefix(
            segmentDistrictFromFullNameReverted(region.full_name_reverted) ||
                segmentDistrictLabelFromEntity(stats?.region_type_district, cityToken) ||
                segmentDistrictLabelFromEntity(region, cityToken),
            cityToken
        );

        if (districtToken && districtToken !== cityToken) {
            districtEventNames.add(`user_segment_district_${districtToken}`);
        }
    }

    return [...Array.from(cityEventNames), ...Array.from(districtEventNames)];
}

// -----------------------------------------------------------------------------
// Event name builders: offer type
// -----------------------------------------------------------------------------

function offerTypeSegmentEvents(selectedTypes: (number | string)[] | null | undefined, config: IOfferTypeSegmentConfig) {
    const selectedOrDefaults = selectedTypes && selectedTypes.length > 0 ? selectedTypes : config.defaultTypes;
    const uniqueOfferTypes = Array.from(new Set(selectedOrDefaults.map((value) => Number(value))));
    const eventNames = new Set<string>();

    for (const offerType of uniqueOfferTypes) {
        const eventName = config.map[offerType];
        if (eventName) eventNames.add(eventName);
    }

    return Array.from(eventNames);
}

// -----------------------------------------------------------------------------
// GTM emitters (public API)
// -----------------------------------------------------------------------------

function pushSegmentEvents(eventNames: string[]) {
    for (const eventName of eventNames) {
        hitGoogleTagManager({event: eventName});
    }
}

export function hitUserSegmentLocationEntry(regions: (IUserSegmentRegionLike | null | undefined)[]) {
    pushSegmentEvents(locationEntrySegmentEvents(regions));
}

export function hitUserSegmentRooms(
    params: {lower?: number | string | null; upper?: number | string | null},
    options?: {maxRooms?: number; minRooms?: number}
) {
    const maxRooms = options?.maxRooms ?? 5;
    const minRooms = options?.minRooms ?? 1;

    pushSegmentEvents(roomsSegmentEvents(params, maxRooms, minRooms));
}

export function hitUserSegmentBudget(params: IHitUserSegmentBudgetParams) {
    if (hasPositiveInstalmentFilter(params.instalment)) return;

    hitGoogleTagManager({event: budgetSegmentEvent(parsePriceUpper(params.priceUpper))});
}

export function hitUserSegmentOfferTypeDefault(selectedTypes: (number | string)[] | null | undefined) {
    pushSegmentEvents(
        offerTypeSegmentEvents(selectedTypes, {
            map: DEFAULT_OFFER_TYPE_SEGMENT_MAP,
            defaultTypes: DEFAULT_OFFER_TYPE_SEGMENT_TYPES
        })
    );
}
import {normalizeToAsciiSnakeCase} from "@pg-mono/string-utils";

import {hitGoogleTagManager} from "./hit_google_tag_manager";

// Releated Task: RP-319

interface IRegionAncestorLike {
    slug?: string;
    name?: string;
}

interface IRegionStatsLike {
    region_type_city?: IRegionAncestorLike | null;
    region_type_district?: IRegionAncestorLike | null;
    region_type_town?: IRegionAncestorLike | null;
}

interface IUserSegmentRegionLike {
    slug?: string;
    name?: string;
    full_name_reverted?: string;
    stats?: IRegionStatsLike | null;
    parent?: {
        slug?: string;
        name?: string;
    } | null;
}

interface IOfferTypeSegmentConfig {
    map: Partial<Record<number, string>>;
    defaultTypes: number[];
}

interface IHitUserSegmentBudgetParams {
    priceUpper?: unknown;
    instalment?: unknown;
}

const DEFAULT_OFFER_TYPE_SEGMENT_MAP: Partial<Record<number, string>> = {
    1: "user_segment_offer_type_mieszkania",
    2: "user_segment_offer_type_domy",
    3: "user_segment_offer_type_lokale",
    4: "user_segment_offer_type_pod_inwestycje"
};

const DEFAULT_OFFER_TYPE_SEGMENT_TYPES = [1, 2];

// -----------------------------------------------------------------------------
// Shared primitives
// -----------------------------------------------------------------------------

function parsePriceUpper(value: unknown) {
    if (value === undefined || value === null || value === "") return null;

    const parsed = typeof value === "number" ? value : parseInt(String(value).replace(/\s/g, ""), 10);

    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function hasPositiveInstalmentFilter(instalment: unknown) {
    return instalment !== undefined && instalment !== "" && instalment !== null && Number(instalment) > 0;
}

// -----------------------------------------------------------------------------
// Event name builders: budget
// -----------------------------------------------------------------------------

function budgetSegmentEvent(priceUpper: number | null) {
    if (priceUpper === null) return "user_segment_budget_bez_limitu";
    if (priceUpper <= 500_000) return "user_segment_budget_0_500k";
    if (priceUpper <= 1_000_000) return "user_segment_budget_500k_1mln";
    if (priceUpper <= 1_500_000) return "user_segment_budget_1mln_1_5mln";
    if (priceUpper <= 2_000_000) return "user_segment_budget_1_5mln_2mln";

    return "user_segment_budget_2mln_plus";
}

// -----------------------------------------------------------------------------
// Event name builders: rooms
// -----------------------------------------------------------------------------

function roomCountToSegmentEvent(roomCount: number) {
    if (roomCount >= 5) return "user_segment_rooms_5_plus";
    if (roomCount <= 1) return "user_segment_rooms_1";
    if (roomCount === 2) return "user_segment_rooms_2";
    if (roomCount === 3) return "user_segment_rooms_3";

    return "user_segment_rooms_4";
}

function roomsSegmentEvents(params: {lower?: number | string | null; upper?: number | string | null}, maxRooms = 5, minRooms = 1) {
    const rawUpper = params.upper !== undefined && params.upper !== "" ? Number(params.upper) : NaN;
    const rawLower = params.lower !== undefined && params.lower !== "" ? Number(params.lower) : NaN;

    const hasUpperBound = Number.isFinite(rawUpper);
    const hasLowerBound = Number.isFinite(rawLower);

    if (!hasUpperBound && !hasLowerBound) return [];

    const collectRange = (minRoomCount: number, maxRoomCount: number) => {
        if (minRoomCount > maxRoomCount) return [];

        const eventNames = new Set<string>();

        for (let roomCount = minRoomCount; roomCount <= maxRoomCount; roomCount++) {
            eventNames.add(roomCountToSegmentEvent(roomCount));
        }

        return Array.from(eventNames);
    };

    if (hasLowerBound && hasUpperBound) {
        return collectRange(Math.round(Math.min(rawLower, rawUpper)), Math.round(Math.max(rawLower, rawUpper)));
    }

    if (hasLowerBound && !hasUpperBound) {
        return collectRange(Math.round(rawLower), maxRooms);
    }

    return collectRange(minRooms, Math.round(rawUpper));
}

// -----------------------------------------------------------------------------
// Event name builders: location
// -----------------------------------------------------------------------------

function segmentLabelFromEntity(value: {name?: string | null; slug?: string | null} | null | undefined) {
    if (!value) return "";

    const raw = value.name?.trim() || value.slug?.trim();

    return raw ? normalizeToAsciiSnakeCase(raw) : "";
}

/** When `full_name_reverted` is "District, City, Voivodeship", the city is the middle segment. */
function segmentCityFromFullNameReverted(fullNameReverted: string | null | undefined) {
    const parts = (fullNameReverted ?? "")
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);
    if (parts.length < 3) return "";

    return normalizeToAsciiSnakeCase(parts[1]);
}

/** When `full_name_reverted` is "District, City, Voivodeship", the district is the first segment. */
function segmentDistrictFromFullNameReverted(fullNameReverted: string | null | undefined) {
    const parts = (fullNameReverted ?? "")
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);
    if (parts.length < 3) return "";

    return normalizeToAsciiSnakeCase(parts[0]);
}

function stripCityTokenPrefix(districtToken: string, cityToken: string) {
    if (!districtToken || !cityToken) return districtToken;

    const prefix = `${cityToken}_`;
    return districtToken.startsWith(prefix) ? districtToken.slice(prefix.length) : districtToken;
}

function segmentDistrictLabelFromEntity(value: {name?: string | null; slug?: string | null} | null | undefined, cityToken: string) {
    if (!value) return "";

    const name = value.name?.trim();
    if (name) return normalizeToAsciiSnakeCase(name);

    const slug = value.slug?.trim();
    if (!slug) return "";

    const hyphenIdx = slug.indexOf("-");
    if (hyphenIdx > 0 && cityToken) {
        const slugCity = normalizeToAsciiSnakeCase(slug.slice(0, hyphenIdx));
        if (slugCity === cityToken) {
            const districtSlug = slug.slice(hyphenIdx + 1);
            if (districtSlug) return normalizeToAsciiSnakeCase(districtSlug);
        }
    }

    return stripCityTokenPrefix(normalizeToAsciiSnakeCase(slug), cityToken);
}

function locationEntrySegmentEvents(regions: (IUserSegmentRegionLike | null | undefined)[]) {
    const cityEventNames = new Set<string>();
    const districtEventNames = new Set<string>();

    for (const region of regions) {
        if (!region) continue;

        const stats = region.stats;
        const cityOrTown = stats?.region_type_city ?? stats?.region_type_town;

        const cityToken =
            segmentLabelFromEntity(cityOrTown) ||
            segmentCityFromFullNameReverted(region.full_name_reverted) ||
            segmentLabelFromEntity(region) ||
            segmentLabelFromEntity(region.parent);

        if (cityToken) {
            cityEventNames.add(`user_segment_city_${cityToken}`);
        }

        const districtToken = stripCityTokenPrefix(
            segmentDistrictFromFullNameReverted(region.full_name_reverted) ||
                segmentDistrictLabelFromEntity(stats?.region_type_district, cityToken) ||
                segmentDistrictLabelFromEntity(region, cityToken),
            cityToken
        );

        if (districtToken && districtToken !== cityToken) {
            districtEventNames.add(`user_segment_district_${districtToken}`);
        }
    }

    return [...Array.from(cityEventNames), ...Array.from(districtEventNames)];
}

// -----------------------------------------------------------------------------
// Event name builders: offer type
// -----------------------------------------------------------------------------

function offerTypeSegmentEvents(selectedTypes: (number | string)[] | null | undefined, config: IOfferTypeSegmentConfig) {
    const selectedOrDefaults = selectedTypes && selectedTypes.length > 0 ? selectedTypes : config.defaultTypes;
    const uniqueOfferTypes = Array.from(new Set(selectedOrDefaults.map((value) => Number(value))));
    const eventNames = new Set<string>();

    for (const offerType of uniqueOfferTypes) {
        const eventName = config.map[offerType];
        if (eventName) eventNames.add(eventName);
    }

    return Array.from(eventNames);
}

// -----------------------------------------------------------------------------
// GTM emitters (public API)
// -----------------------------------------------------------------------------

function pushSegmentEvents(eventNames: string[]) {
    for (const eventName of eventNames) {
        hitGoogleTagManager({event: eventName});
    }
}

export function hitUserSegmentLocationEntry(regions: (IUserSegmentRegionLike | null | undefined)[]) {
    pushSegmentEvents(locationEntrySegmentEvents(regions));
}

export function hitUserSegmentRooms(
    params: {lower?: number | string | null; upper?: number | string | null},
    options?: {maxRooms?: number; minRooms?: number}
) {
    const maxRooms = options?.maxRooms ?? 5;
    const minRooms = options?.minRooms ?? 1;

    pushSegmentEvents(roomsSegmentEvents(params, maxRooms, minRooms));
}

export function hitUserSegmentBudget(params: IHitUserSegmentBudgetParams) {
    if (hasPositiveInstalmentFilter(params.instalment)) return;

    hitGoogleTagManager({event: budgetSegmentEvent(parsePriceUpper(params.priceUpper))});
}

export function hitUserSegmentOfferTypeDefault(selectedTypes: (number | string)[] | null | undefined) {
    pushSegmentEvents(
        offerTypeSegmentEvents(selectedTypes, {
            map: DEFAULT_OFFER_TYPE_SEGMENT_MAP,
            defaultTypes: DEFAULT_OFFER_TYPE_SEGMENT_TYPES
        })
    );
}
